import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2, Shield, Package, Fingerprint, Copy } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function BuyerVerifyPage() {
  const { fingerprint } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/verify/${encodeURIComponent(fingerprint)}`);
        setData(await r.json());
      } catch {
        setData({ verified: false, message: 'Could not reach verification service.' });
      } finally { setLoading(false); }
    })();
  }, [fingerprint]);

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-emerald-50 dark:from-slate-950 dark:to-emerald-950">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  const verified = data?.verified;
  const bg = verified ? 'from-emerald-50 to-blue-50 dark:from-emerald-950/40 dark:to-blue-950/40' : 'from-rose-50 to-orange-50 dark:from-rose-950/40 dark:to-orange-950/40';

  return (
    <div className={`min-h-screen bg-gradient-to-br ${bg} py-16 px-6`} data-testid="buyer-verify-page">
      <div className="max-w-2xl mx-auto">
        {/* Brand strip */}
        <div className="flex items-center gap-2 mb-8">
          <Shield className="w-6 h-6 text-emerald-600" />
          <p className="text-lg font-semibold tracking-tight">SelleSend Record Ledger</p>
        </div>

        {/* Verdict card */}
        <div className={`rounded-3xl border-2 p-8 shadow-lg ${verified ? 'border-emerald-300 bg-white dark:bg-slate-900' : 'border-rose-300 bg-white dark:bg-slate-900'}`}>
          <div className="flex items-start gap-4">
            {verified ? (
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-blue-500 flex items-center justify-center shrink-0">
                <CheckCircle2 className="w-9 h-9 text-white" strokeWidth={2.5} />
              </div>
            ) : (
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-rose-500 to-orange-500 flex items-center justify-center shrink-0">
                <XCircle className="w-9 h-9 text-white" strokeWidth={2.5} />
              </div>
            )}
            <div className="flex-1">
              <h1 className="text-3xl font-semibold tracking-tight mb-1" data-testid="verify-verdict">
                {verified ? 'Fingerprint verified' : 'Fingerprint not found'}
              </h1>
              <p className="text-slate-600 dark:text-slate-400">{data.message}</p>
            </div>
          </div>

          {verified && (
            <div className="mt-8 grid gap-4">
              <Row label="Registered on">
                <span className="tabular-nums">{new Date(data.registered_at).toLocaleString()}</span>
              </Row>
              <Row label="Ledger">{data.ledger}</Row>
              <Row label="Data hash"><code className="font-mono text-xs break-all">{data.data_hash}</code></Row>
              {data.anchor ? (
                <>
                  <Row label="Merkle root">
                    <code className="font-mono text-xs break-all">{data.anchor.merkle_root}</code>
                  </Row>
                  <Row label="Proof">
                    <a href={data.anchor.proof_url} className="text-emerald-600 hover:underline">
                      Check it yourself
                    </a>
                  </Row>
                </>
              ) : (
                <Row label="Merkle root">
                  <span className="text-slate-500">Not rolled up yet. Anchors run every 24 hours.</span>
                </Row>
              )}
              {data.dimensions?.length && (
                <Row label="Dimensions">
                  <span className="tabular-nums">
                    {data.dimensions.length}&quot; × {data.dimensions.width}&quot; × {data.dimensions.height}&quot; {data.dimensions.unit}
                  </span>
                </Row>
              )}
              {data.weight_oz != null && (
                <Row label="Weight">
                  <span className="tabular-nums">
                    {data.weight_oz.toFixed(1)} oz · {(data.weight_oz / 16).toFixed(2)} lb · {(data.weight_oz / 35.274).toFixed(2)} kg
                  </span>
                </Row>
              )}
              {data.item_category && <Row label="Category">{data.item_category.replace(/_/g, ' ')}</Row>}
            </div>
          )}

          {/* Share row */}
          <div className="mt-8 pt-6 border-t border-slate-100 dark:border-slate-800 flex items-center gap-2">
            <Fingerprint className="w-4 h-4 text-slate-400" />
            <code className="flex-1 text-xs font-mono text-slate-500 truncate">{fingerprint}</code>
            <button
              onClick={copyLink}
              className="text-xs px-3 py-1.5 rounded-md bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 flex items-center gap-1"
              data-testid="copy-link-btn"
            >
              <Copy className="w-3 h-3" /> {copied ? 'Copied' : 'Copy this URL'}
            </button>
          </div>
        </div>

        {/* Trust footer */}
        {verified && (data.what_this_shows || data.what_this_does_not_show) && (
          <div className="mt-6 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white/70 dark:bg-slate-900/70 p-5 text-sm">
            {data.what_this_shows && (
              <p className="text-slate-700 dark:text-slate-300">
                <span className="font-semibold">What this shows: </span>{data.what_this_shows}
              </p>
            )}
            {data.what_this_does_not_show && (
              <p className="mt-3 text-slate-600 dark:text-slate-400">
                <span className="font-semibold">What it does not: </span>{data.what_this_does_not_show}
              </p>
            )}
          </div>
        )}

        <p className="text-center text-xs text-slate-500 mt-6" data-testid="verify-footer">
          No login required. Anyone with this URL sees the same result.
        </p>
      </div>
    </div>
  );
}

const Row = ({ label, children }) => (
  <div className="flex items-baseline justify-between gap-4 border-b border-slate-100 dark:border-slate-800 pb-3 last:border-0 last:pb-0">
    <span className="text-xs uppercase tracking-wider text-slate-500">{label}</span>
    <span className="text-sm text-right">{children}</span>
  </div>
);
